import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { BookOpen, Search, GraduationCap, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CoursesPage() {
  const { user, getAuthHeaders } = useAuth();
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchCourses = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API_URL}/api/courses`, { headers });
      setCourses(response.data);
      setFilteredCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  useEffect(() => {
    if (search) {
      setFilteredCourses(
        courses.filter(c => 
          c.title.toLowerCase().includes(search.toLowerCase()) ||
          c.description.toLowerCase().includes(search.toLowerCase())
        )
      );
    } else {
      setFilteredCourses(courses);
    }
  }, [search, courses]);

  const regularCourses = filteredCourses.filter(c => !c.is_capstone);
  const capstoneCourse = filteredCourses.find(c => c.is_capstone);

  if (loading) {
    return (
      <div className="flex min-h-screen pt-14 lg:pt-0">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 bg-background">
          <div className="animate-pulse">Loading courses...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen pt-14 lg:pt-0" data-testid="courses-page">
      <Sidebar />
      <main className="flex-1 p-4 sm:p-6 lg:p-8 bg-background overflow-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light text-primary mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Program Courses
          </h1>
          <p className="text-muted-foreground">
            {user?.program === 'doctor' ? 'Doctor of Humanitarian Action & Development' : user?.program === 'mdppm' ? 'Master of Development Project & Program Management' : 'Master of Humanitarian Action & Development'}
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-8 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search courses..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="course-search-input"
          />
        </div>

        {/* Regular Courses */}
        <div className="mb-8">
          <h2 className="text-xl font-medium text-primary mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Core Courses ({regularCourses.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regularCourses.map((course, index) => (
              <Link
                key={course.id}
                to={`/courses/${course.id}`}
                className="group"
                data-testid={`course-item-${index}`}
              >
                <Card className="border border-border shadow-none hover:shadow-md transition-all duration-200 overflow-hidden h-full">
                  <div className="h-40 bg-muted overflow-hidden">
                    {course.image_url ? (
                      <img
                        src={course.image_url}
                        alt={course.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="w-12 h-12 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="px-2 py-0.5 bg-secondary text-xs font-medium rounded">
                        Course {course.order}
                      </span>
                      <span className="px-2 py-0.5 bg-muted text-xs text-muted-foreground rounded">
                        {course.duration || '1 Month'}
                      </span>
                    </div>
                    <h3 className="text-lg font-medium text-primary mb-2 group-hover:text-primary/80" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      {course.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {course.description}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>

        {/* Capstone Project */}
        {capstoneCourse && (
          <div>
            <h2 className="text-xl font-medium text-primary mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Capstone Project
            </h2>
            <Link
              to={`/courses/${capstoneCourse.id}`}
              className="group block"
              data-testid="capstone-course"
            >
              <Card className="border-2 border-primary/20 shadow-none hover:shadow-md transition-all duration-200 overflow-hidden">
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-1/3 h-48 md:h-auto bg-muted overflow-hidden">
                    {capstoneCourse.image_url ? (
                      <img
                        src={capstoneCourse.image_url}
                        alt={capstoneCourse.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <GraduationCap className="w-16 h-16 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <CardContent className="flex-1 p-6 flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded">
                        Final Project
                      </span>
                      <span className="px-3 py-1 bg-muted text-xs text-muted-foreground rounded flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {capstoneCourse.duration || '3 Months'}
                      </span>
                    </div>
                    <h3 className="text-2xl font-medium text-primary mb-3 group-hover:text-primary/80" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      {capstoneCourse.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {capstoneCourse.description}
                    </p>
                  </CardContent>
                </div>
              </Card>
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
